// Authorised image: fetches an API image (session cookie) into a blob URL, revoked on unmount; skeleton while loading, problem on error.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { apiFetch } from "@/api/client";
import { ApiProblem } from "@/api/problem";
import { ProblemState } from "@/components/common/ProblemState";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";

async function fetchBlob(path: string, signal: AbortSignal): Promise<Blob> {
  const response = await apiFetch(path, { signal });
  if (!response.ok) {
    throw await ApiProblem.fromResponse(response);
  }
  return response.blob();
}

export function useBlobUrl(path: string | null): {
  url: string | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["blob", path],
    queryFn: ({ signal }) => fetchBlob(path ?? "", signal),
    enabled: path !== null,
    staleTime: Infinity,
    gcTime: 5 * 60_000,
  });
  const url = useMemo(() => (query.data ? URL.createObjectURL(query.data) : null), [query.data]);
  useEffect(
    () => () => {
      if (url !== null) {
        URL.revokeObjectURL(url);
      }
    },
    [url],
  );
  return {
    url,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

interface BlobImageProps {
  path: string;
  alt: string;
  className?: string;
  imgClassName?: string;
}

export function BlobImage({ path, alt, className, imgClassName }: BlobImageProps) {
  const { url, isLoading, error, refetch } = useBlobUrl(path);
  if (error) {
    return <ProblemState problem={error} retry={refetch} compact className={className} />;
  }
  if (isLoading || url === null) {
    return <Skeleton className={cn("aspect-[4/3] w-full", className)} />;
  }
  return (
    <span className={cn("block", className)}>
      <img src={url} alt={alt} className={cn("block h-auto max-w-full", imgClassName)} />
    </span>
  );
}
